import React, { useEffect, useState } from 'react';
import api from '../../../lib/api';
import { Card } from '../../../components/ui/card';
import { Loader2 } from 'lucide-react';
import { fmtDate } from './demoConstants';
import DemoPeriodSelector from './DemoPeriodSelector';

const Kpi = ({ label, value, testid }) => (
  <Card className="p-3 border" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
    <div className="text-xl font-bold text-slate-900">{value}</div>
  </Card>
);

const tone = (v, warn, bad) => (v >= bad ? 'text-rose-600 font-semibold' : v >= warn ? 'text-amber-600 font-semibold' : 'text-slate-700');

const Detail = ({ rows }) => (
  <Card className="border overflow-x-auto" data-testid="late-entry-detail">
    <div className="px-4 pt-3 text-sm font-bold text-slate-900">Flagged demos</div>
    <table className="w-full text-xs mt-2">
      <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500"><tr>
        {['Demo', 'Demo date', 'Entered on', 'Entry delay', 'Counsellor', 'Trainer', 'Student', 'Invoice', 'Invoice before demo'].map(h => <th key={h} className="text-left px-3 py-2">{h}</th>)}
      </tr></thead>
      <tbody>{rows.map(r => (
        <tr key={r.id} className="border-t border-slate-100" data-testid={`late-detail-${r.id}`}>
          <td className="px-3 py-2 font-mono">{r.demo_code}</td><td className="px-3">{fmtDate(r.demo_date)}</td><td className="px-3">{fmtDate(r.entered_on)}</td>
          <td className={`px-3 ${tone(r.entry_lag_days || 0, 3, 8)}`}>{r.entry_lag_days > 0 ? `${r.entry_lag_days}d late` : 'on time'}</td>
          <td className="px-3">{r.counsellor_name}</td><td className="px-3">{r.trainer_name}</td><td className="px-3">{r.student_name || '—'}</td>
          <td className="px-3 font-mono">{r.invoice_number || '—'}</td>
          <td className="px-3">{r.invoice_days_before_demo != null ? <span className={r.invoice_counted ? 'text-amber-700' : 'text-rose-600'}>{r.invoice_days_before_demo}d · {r.invoice_counted ? 'counted' : 'not counted'}</span> : '—'}</td>
        </tr>
      ))}</tbody>
    </table>
    {rows.length === 0 && <p className="text-center text-sm text-slate-400 py-6">No late entries in this period.</p>}
  </Card>
);

export default function DemoLateEntryPage() {
  const [period, setPeriod] = useState(null);
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!period) return;
    setD(null);
    api.get('/training/demos/reports/late-entry', { params: { date_from: period.from_date, date_to: period.to_date } }).then(r => setD(r.data));
  }, [period]);
  return (
    <div className="flex flex-col gap-5" data-testid="late-entry-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="text-sm text-slate-500 max-w-2xl">Which counsellors enter demos late. A demo counts as late when it is entered more than {d?.late_days ?? 2} days after the demo date, or when the admission invoice is dated before the demo.</p>
        <DemoPeriodSelector onChange={setPeriod} defaultKey="this_month" />
      </div>
      {!d ? <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : <>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi label="Demos" value={d.totals.demos} testid="late-kpi-demos" />
          <Kpi label={`Entered > ${d.late_days}d late`} value={d.totals.late_entries} testid="late-kpi-late" />
          <Kpi label="Admissions" value={d.totals.admissions} testid="late-kpi-admissions" />
          <Kpi label="Invoice before demo" value={d.totals.invoice_before_demo} testid="late-kpi-invoice-before" />
        </div>
        <Card className="border overflow-x-auto" data-testid="late-entry-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500"><tr>
              {['Counsellor', 'Demos', 'Late entries', 'Late %', 'Avg delay', 'Worst delay', 'Admissions', 'Invoice before demo', '% of admissions', 'Counted (≤15d)', 'Not counted'].map(h => <th key={h} className="text-left px-3 py-2">{h}</th>)}
            </tr></thead>
            <tbody>{d.rows.map((r, i) => (
              <tr key={r.counsellor_id || r.counsellor_name} className="border-t border-slate-100" data-testid={`late-row-${r.counsellor_id || i}`}>
                <td className="px-3 py-2 font-medium text-slate-900">{i === 0 && (r.late_entries + r.invoice_before_demo) > 0 ? '⚑ ' : ''}{r.counsellor_name}</td>
                <td className="px-3">{r.demos}</td><td className={`px-3 ${tone(r.late_entries, 1, 3)}`}>{r.late_entries}</td>
                <td className={`px-3 ${tone(r.late_pct, 10, 25)}`}>{r.late_pct}%</td><td className="px-3">{r.avg_lag}d</td><td className="px-3">{r.max_lag}d</td>
                <td className="px-3">{r.admissions}</td><td className={`px-3 ${tone(r.invoice_before_demo, 1, 3)}`}>{r.invoice_before_demo}</td>
                <td className={`px-3 ${tone(r.invoice_before_pct, 10, 25)}`}>{r.invoice_before_pct}%</td><td className="px-3">{r.counted_in_grace}</td><td className="px-3">{r.not_counted}</td>
              </tr>
            ))}</tbody>
          </table>
        </Card>
        <Detail rows={d.detail} />
      </>}
    </div>
  );
}
